import React, { useEffect, useState, useRef } from 'react';
import { useHistory } from 'react-router-dom';

import './mobile-brand-product-list-template-style.scss';

import SortListIcon from '@src/componentsNew/Icons/Icons/SortListIcon';
import FilterListIcon from '@src/componentsNew/Icons/Icons/FilterListIcon';
import SearchIcon from '@src/componentsNew/Icons/Icons/SearchIcon';
import WishlistIcon from '@src/componentsNew/Icons/Icons/WishlistIcon';
import CartQuantityIcon from '@src/componentsNew/Icons/CartQuantityIcon/CartQuantityIcon';

import Typography from '@src/componentsNew/Typography/Typography';
import Button from '@src/componentsNew/Buttons/Button';
import Carousel from '@src/componentsNew/Carousel/Carousel';
import BasicModal from '@src/componentsNew/Modals/BasicModal/BasicModal';
import MobileCustomHeader from '@src/componentsNew/Header/CustomHeader/MobileCustomHeader';
import MobileProductListSortingFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListSortingFilter/MobileProductListSortingFilter';
import MobileProductListFilter from '@src/componentsNew/Modals/FilterModal/MobileProductListFilter/MobileProductListFilter';
import MobileBasicProductList from '@src/componentsNew/ProductList/Basic/MobileBasicProductList';

import useIsComDomain from '@src/hooks/useIsComDomain';
import useScrollDirection from '@src/hooks/useScrollDirection';

import { productsVariables } from '@components/ProductPage/ProductList/__generated__/products';
import { StockAvailability, ProductOrderField, OrderDirection } from '__generated__/globalTypes';
import {
  brand_brand_images,
  brand_brand_categories_edges,
} from '@src/components/ProductPage/BrandPage/__generated__/brand';
import { parsingBrandCategoryData } from '@src/utils';
import { getInvisibleBrandIds, MOBILE_PRODUCT_LIST_REQUEST_COUNT } from '@src/utils/constants';
import { wishListUrl, searchUrl, cartUrl } from '@src/app/routes';
import { getDBIdFromGraphqlId } from "@utils";

type IProps = {
  brandName: string;
  brandTnc: string;
  brandImages?: brand_brand_images[];
  brandCategories?: brand_brand_categories_edges[];
  currentBrandGraphqlId?: string;
};

const MobileBrandProductListTemplate: React.FC<IProps> = ({
  brandName,
  brandTnc,
  brandImages,
  brandCategories,
  currentBrandGraphqlId,
}) => {
  const history = useHistory();
  const isComDomain = useIsComDomain();

  const initialVariables = {
    brand: currentBrandGraphqlId,
    stockAvailability: StockAvailability.IN_STOCK,
    sortBy: {
      field: ProductOrderField.POPULARITY,
      direction: OrderDirection.DESC,
    },
    first: MOBILE_PRODUCT_LIST_REQUEST_COUNT,
    ...(isComDomain && { isMgs: true }),
  };

  const parsedBrandCategories = parsingBrandCategoryData(brandCategories);
  const { currentScrollY, scrollDirection } = useScrollDirection('up');

  const [variables, setVariables] = useState<productsVariables>(initialVariables);
  const [numOfProducts, setNumOfProducts] = useState<number>(0);
  const [seletedCategoryId, setSelectedCategoryId] = useState(null);

  const [isTncModalOpen, setIsTncModalOpen] = useState(false);
  const [isSortingFilterModalOpen, setIsSortingFilterModalOpen] = useState(false);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);

  const brandImageRef = useRef(null);

  useEffect(() => {
    setVariables((prev) => ({
      ...prev,
      brand: currentBrandGraphqlId,
    }));
  }, [currentBrandGraphqlId]);

  const handleTncModalOpen = () => {
    setIsTncModalOpen(true);
  };

  const handleTncModalClose = () => {
    setIsTncModalOpen(false);
  };

  const handleSortingFilterModalOpen = () => {
    setIsSortingFilterModalOpen(true);
  };

  const handleSortingFilterModalClose = () => {
    setIsSortingFilterModalOpen(false);
  };

  const handleFilterModalOpen = () => {
    setIsFilterModalOpen(true);
  };

  const handleFilterModalClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleAllCategoryClick = () => {
    setSelectedCategoryId(null);

    setVariables((prev) => {
      const updatedVariables = { ...prev };

      if ('categories' in updatedVariables) {
        delete updatedVariables['categories'];
      }

      return updatedVariables;
    });
  };

  const handleCategoryClick = (categoryId) => {
    setSelectedCategoryId(categoryId);

    setVariables((prev) => ({
      ...prev,
      categories: categoryId,
    }));
  };

  const brandDbId = getDBIdFromGraphqlId(currentBrandGraphqlId, 'Brand')?.toString();
  if (getInvisibleBrandIds().includes(brandDbId)) {
    return null;
  }

  return (
    <div className="mobile-brand-product-list-template-wrapper">
      <div className="fixed-area">
        <MobileCustomHeader
          type="center"
          isShowBackBtn={true}
          backBtnColor="#21242A7A"
          rightChild={
            <>
              <SearchIcon onClick={() => history.push(searchUrl)} />
              <WishlistIcon onClick={() => history.push(wishListUrl)} />
              <CartQuantityIcon isMobile={true} onClick={() => history.push(cartUrl)} />
            </>
          }
        />
      </div>

      <div className="content-area">
        {brandImages?.length ? (
          <div className="brand-image-wrapper" ref={brandImageRef}>
            <Carousel
              customSetting={{
                dots: true,
                infinite: true,
                centerMode: true,
                centerPadding: '0',
                autoplay: true,
                autoplaySpeed: 4000,
                speed: 2000,
                slidesToShow: 1,
                slidesToScroll: 1,
                className: 'brand-slider',
              }}
            >
              {brandImages.map((item) => (
                <img key={item.mobileUrl} className="brand-image" src={item.mobileUrl} alt={item?.alt ?? ''} />
              ))}
            </Carousel>
            {brandTnc ? (
              <Typography cName="tnc-btn" variant="body3" component="span" onClick={handleTncModalOpen}>
                T&Cs
              </Typography>
            ) : null}
          </div>
        ) : null}

        <div
          className={`sticky-area ${
            scrollDirection === 'up' || currentScrollY <= brandImageRef?.current?.offsetHeight + 10 ? 'up' : 'down'
          }`}
        >
          <div className="brand-name-wrapper">
            <Typography variant="h6" component="h6">
              {brandName}
            </Typography>
          </div>

          {parsedBrandCategories?.length > 0 ? (
            <div className="category-area">
              <Button size="large" cName={`${!seletedCategoryId ? 'selected' : ''}`} onClick={handleAllCategoryClick}>
                <Typography cName="category-name" variant="subtitle2" component="span" style={{ color: '#11141D99' }}>
                  All
                </Typography>
              </Button>
              {parsedBrandCategories?.map((level1Category) =>
                level1Category?.child.map((level2Category) => (
                  <Button
                    key={level2Category.id}
                    size="large"
                    cName={`${seletedCategoryId === level2Category.id ? 'selected' : ''}`}
                    onClick={() => handleCategoryClick(level2Category?.id)}
                  >
                    <Typography
                      cName="category-name"
                      variant="subtitle2"
                      component="span"
                      style={{ color: '#11141D99' }}
                    >
                      {level2Category.name}
                    </Typography>
                  </Button>
                )),
              )}
            </div>
          ) : null}
        </div>

        <div className="sorting-filter-area">
          <div className="total-count-area">
            <Typography
              variant="subtitle2"
              component="span"
              style={{ color: '#11141D99' }}
            >{`${numOfProducts}`}</Typography>
            <Typography variant="body3" component="span" style={{ color: '#11141D99' }}>
              Products
            </Typography>
          </div>

          <div className="sorting-icon-wrapper">
            <SortListIcon onClick={handleSortingFilterModalOpen} />
            <FilterListIcon onClick={handleFilterModalOpen} />
          </div>
        </div>

        <MobileBasicProductList
          initialVariables={initialVariables}
          variables={variables}
          setNumOfProducts={setNumOfProducts}
        />

        {isTncModalOpen && (
          <BasicModal
            title="T&Cs"
            titleColor="#212121"
            content={brandTnc}
            contentColor="#666666"
            open={isTncModalOpen}
            onClose={handleTncModalClose}
            closeButtonText="close"
            showCloseIcon={false}
          />
        )}

        {isSortingFilterModalOpen && (
          <MobileProductListSortingFilter
            open={isSortingFilterModalOpen}
            onClose={handleSortingFilterModalClose}
            variables={variables}
            setVariables={setVariables}
          />
        )}

        {isFilterModalOpen && (
          <MobileProductListFilter
            open={isFilterModalOpen}
            onClose={handleFilterModalClose}
            variables={variables}
            setVariables={setVariables}
          />
        )}
      </div>
    </div>
  );
};

export default MobileBrandProductListTemplate;
